<template>
	<div class="game-fps__performance__game" :class="{ 'step3-game-fps': isStep3 }">
		<div class="game-fps__performance__game__selected">
			<div class="game-fps__performance__game__cover text-center">
				<img
					:src="game[imageKey]"
					:alt="`thumbnail image for ${game.name} game`"
					class="selected"
				>

				<div v-if="!fpsText" class="tooltip-container">
					<tooltip class-binding="fps-tooltip">
						{{ fpsPendingMessage }}
					</tooltip>
				</div>
			</div>

			<div class="game-fps__performance__game__description">
				<h3 class="game-fps__performance__game__title">{{ game.name }}</h3>
				<h4 v-if="game.vsync == 1" class="vsync-compatible m-a-0 text-light text-purple">V-SYNC</h4>
				<div
					:class="[ color, { 'default-fps': onlyRecommendedFps } ]"
					class="game-fps__performance__game__fps-bar"
				>
					<h4 v-if="fpsText" class="number" :style="getProgressBarPercentageClass('margin-left')">
						{{ fpsText }}
						<span class="small text-light">
							fps {{ onlyRecommendedFps ? "recommended" : "" }}
						</span>
					</h4>

					<h4 v-else class="game-fps__performance__game__pending">
						<span>FPS</span> pending

						<div class="tooltip-container">
							<tooltip class-binding="fps-tooltip">
								{{ fpsPendingMessage }}
							</tooltip>
						</div>
					</h4>

					<div class="progress">
						<div
							:style="getProgressBarPercentageClass('width')"
							:aria-valuenow="fps"
							:aria-valuemin="fps * 0.9"
							:aria-valuemax="fps * 1.10"
							class="progress-bar bar-meter"
							:class="{'bar-meter--pending': !fpsText}"
							role="progressbar"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import CONSTANTS from "@/config/constants";
import Tooltip from "@c/generals/tooltip";

export default {
	name: "GameFps",
	components: {
		Tooltip
	},
	props: {
		isStep3: {
			type: Boolean,
			required: false,
			default: false
		},
		game: {
			type: Object,
			required: true
		},
		fps: {
			type: Number,
			default: 0
		},
		onlyRecommendedFps: {
			type: Boolean,
			default: false
		},
		imageKey: {
			type: String,
			default: "image_url"
		}
	},
	data() {
		return {
			fpsPendingMessage: CONSTANTS.FPS_PENDING_MESSAGE
		};
	},
	computed: {
		color() {
			if (this.fps >= 60) {
				return "game-fps__performance__game__fps-bar--success";
			}

			if (this.fps > 20) {
				return "game-fps__performance__game__fps-bar--warning";
			} else {
				return "game-fps__performance__game__fps-bar--danger";
			}
		},
		fpsText() {
			if (this.onlyRecommendedFps) {
				return 60;
			}

			return this.fps;
		}
	},
	methods: {
		getProgressBarPercentageClass(measure) {
			let gameFps = (this.fps / 220) * 100;
			if (gameFps > 72.9243 && measure == "margin-left") {
				gameFps = 72.9243;
			}
			return `${measure}:${gameFps}%;`;
		}
	}
};
</script>

<style lang="scss" scoped>
@import "~@s/_asset-mixers.scss";

.game-fps__performance__game {
	position: relative;
	margin-bottom: 20px;

	&__selected {
		display: flex;
		flex-flow: row;
		justify-content: flex-start;
		align-content: space-between;
	}

	&__cover {
		width: 95px;
		height: 120px;
		margin-right: 20px;

		img {
			width: 95px;
			max-height: 100%;
			margin-right: 35px;
		}
	}

	&__description {
		display: flex;
		justify-content: space-between;
		flex-grow: 2;
		flex-direction: column;
	}

	&__title {
		color: $white;
		font-family: Akkurat-Light;
		font-size: 20px;
		font-weight: 300;
		line-height: 22px;
		margin: 0;
	}

	&__pending {
		color: $white;
		font-size: 20px;
		font-weight: bold;
		letter-spacing: 0;
		margin: 0 0 10px;
		display: inline-block;

		@media (max-width: 991px) {
			margin: 5px 0 0;
			text-transform: capitalize;
			font-family: Akkurat-Light;
			font-weight: 300;
			font-size: 14px;
			letter-spacing: 0;

			span {
				display: none;
			}
		}
	}

	&__fps-bar {
		max-width: 495px;
		color: $white;

		&--success {
			color: #26FF5C;
		}

		&--warning {
			color: #C77F09;
		}

		&--danger {
			color: #A53E4B;
		}

		.progress {
			margin: 0;
			height: 12px;
			border-radius: 6px;
			width: 100%;
			background-color: #232323;
		}

		.number {
			margin-top: 0;
			margin-bottom: 5px;
			color: currentColor;
			font-size: 24px;
			font-weight: bold;

			span {
				font-size: 20px;
				font-family: Akkurat-Light;
				font-weight: 300;
				color: $white;
			}
		}

		.bar-meter {
			color: currentColor;
			background-color: currentColor;
			border-radius: 6px;
		}

		.bar-meter--pending {
			width: 50% !important;
			background: #5F6061;
		}

		&.default-fps {
			.number {
				color: $white;
			}
			.bar-meter {
				color: #26FF5C;
				width: 27.7273% !important;
			}
		}
	}

	.tooltip-container {
		position: absolute;
		z-index: 10;
		display: none;
		right: 38%;
		top: 60%;

		font-size: 16px;
		letter-spacing: 0;
		line-height: 21px;
		text-align: left;
		font-weight: normal;
	}

	&__cover:hover .tooltip-container,
	&__pending:hover .tooltip-container {
		display: block;
	}
}

[data-config-step="3"] {
	.game-fps__performance__game {

		&__cover {
			width: 95px;
			height: 120px;
			margin-right: 35px;

			@media (min-width: 767px) and (max-width: 1200px) {
				margin-right: 15px;
				height: 100px;
				width: auto;
			}

			img {
				width: 100%;
				width: 95px;
				max-height: 100%;
				margin-right: 35px;

				@media (max-width: 1200px) {
					margin: 0 auto;
					width: auto;
				}

				@media (min-width: 580px) and (max-width: 991px) {
					height: 110px;
				}

				@media (max-width: 580px) {
					height: 70px;
					width: 55px;
				}
			}
		}
	}

	@media (max-width: 991px) {
		.game-fps__performance__game {
			margin-bottom: 0;

			&:not(:first-child) {
				margin-left: 32px;
			}

			&__selected {
				display: flex;
				flex-direction: column;
				text-align: center;
			}

			.progress {
				display: none;
			}

			&__title {
				display: none;
			}

			&__cover {
				margin-right: 0;
				width: auto;
				height: auto;
				margin-bottom: 10px;
			}

			&__fps-bar {
				.number {
					margin-left: 0 !important;
					font-size: 20px;
					span {
						font-size: 14px;
					}
				}
			}
		}
	}
}

[data-config-step="4"] {
	.game-fps__performance__game {
			margin-bottom: 10px;

			&__title {
				display: none;
			}

			&__fps-bar {
			.progress {
				display: none;
			}

			.number {
				margin-left: 0 !important;
			}
		}

		&__cover {
			margin-right: 0;

			img {
				height: 110px;
				width: auto;
			}
		}

		@media (min-width: 991px) and (max-width: 1200px) {
			margin-bottom: 10px;
			&__cover {
				margin-right: 10px;
				height: 100px;
				width: auto;

				img {
					width: 75px;
					margin-right: 0;
				}
			}

			&__fps-bar {
				.number {
					font-size: 22px;

					span {
						font-size: 15px;
					}
				}
			}
		}

		.tooltip-container {
			right: -155%;
			top: 80%;
		}
	}
}

[data-config-step="5"] {
	.game-fps__performance__game {
		margin-bottom: 0;

		&:not(:first-child) {
			margin-left: 32px;

			@media (min-width: 991px) and (max-width: 1200px) {
				margin-left: 15px;
			}
		}

		&__selected {
			display: flex;
			flex-direction: column;
			text-align: center;
		}

		.progress {
			display: none;
		}

		&__title {
			display: none;
		}

		&__cover {
			margin-right: 0;
			width: auto;
			height: auto;
			margin-bottom: 10px;

			img {
				width: auto;
				margin: 0 auto;
				height: 110px;
				max-height: 100%;
				object-fit: cover;

				@media (min-width: 991px) and (max-width: 1200px) {
					height: 80px;
				}


			}
		}

		&__fps-bar {
			.number {
				margin-left: 0 !important;
				font-size: 20px;
				span {
					font-size: 14px;
				}
			}
		}

		&__pending {
			font-family: Akkurat-Light;
			font-size: 14px;
			letter-spacing: 0;
			font-weight: 300;
		}

		.tooltip-container {
			right: -115%;
			top: 170%;

			@media (max-width: 1500px) {
				right: -300%;
			}
		}
	}
}
</style>